/**
 * How a pattern's lines cover a pixel (STYLE §15.3), mirrored by patterns.glsl. A preset keeps the
 * surface's mean colour only while its lines cover, on average, the share its modules paid for:
 * the box filter below does that at any footprint; the Kuwahara pass paints over whatever is
 * narrower than its sectors, so a thin line is drawn wide enough to outlast it, and a family too
 * dense for that fades into the mean.
 */

import { smoothstep } from '../../core/math';

/* Up close a hand-drawn line keeps a soft edge a quarter of its width wide. */
const EDGE_SOFTNESS = 0.25;

/** The share of a pixel footprint metres wide that a line width metres wide covers, its centre d
 *  metres from the pixel's: the box filter of the footprint. */
export function lineCoverage(d: number, width: number, footprint: number): number {
  const f = Math.max(footprint, EDGE_SOFTNESS * width + 1e-6);
  const lo = Math.max(Math.abs(d) - 0.5 * width, -0.5 * f);
  const hi = Math.min(Math.abs(d) + 0.5 * width, 0.5 * f);
  return Math.min(Math.max((hi - lo) / f, 0), 1);
}

/** How far v lies from the nearest line of a family that lies at the foot of each cell (from foot
 *  to foot + width, cells period apart). A pixel just below a cell's foot reaches that cell's line,
 *  so the next cell's line counts as well as v's own. */
export function footLineOffset(v: number, foot: number, width: number, period: number): number {
  const d = Math.abs(v - foot - 0.5 * width);
  return Math.min(d, Math.abs(d - period));
}

/** The cover of two families of lines crossing at right angles: the footprint's box filter is
 *  separable, so where both reach a pixel their overlap is the product of their covers. */
export function unionCover(a: number, b: number): number {
  return a + b - a * b;
}

/** The side, in pixels, of the Kuwahara pass's sectors: it paints each pixel from the calmest of
 *  four sectors radius + 1 pixels square, so a line narrower than a sector is painted over. No pass
 *  runs at radius 0. */
export function paintSectorPx(kuwaharaRadius: number): number {
  return kuwaharaRadius > 0 ? kuwaharaRadius + 1 : 0;
}

/** The width a line is drawn at: one narrower than the paint pass keeps is drawn a sector and a
 *  pixel wide, which the pass keeps, and paintedLine lowers its contrast to match. */
export function drawnWidth(width: number, footprint: number, sectorPx: number): number {
  return sectorPx > 0 ? Math.max(width, (sectorPx + 1) * footprint) : width;
}

/** A line's cover as drawn: at its drawn width and at the contrast that keeps its share. */
export function paintedLine(d: number, width: number, footprint: number, sectorPx: number): number {
  const drawn = drawnWidth(width, footprint, sectorPx);
  return (lineCoverage(d, drawn, footprint) * width) / drawn;
}

/** How much of a family of lines a period apart the pattern draws: the pass keeps a drawn line and
 *  the gap after it only while each is a sector and a pixel wide, and paints a narrower gap over
 *  as surely as a narrower line; below that the family fades into the kept mean. */
export function lineKept(period: number, footprint: number, sectorPx: number): number {
  if (sectorPx <= 0) return 1;
  return smoothstep(2 * sectorPx + 1, 2 * sectorPx + 2, period / Math.max(footprint, 1e-6));
}
